import { useState, useEffect } from "react";

export type Language = "pl" | "en";

/**
 * Hook to detect browser language and return appropriate language code
 * Returns 'pl' for Polish browsers, 'en' for all others
 */
export function useBrowserLanguage(): Language {
  const [language, setLanguage] = useState<Language>("en");

  useEffect(() => {
    const detectLanguage = (): Language => {
      // Check navigator.language (primary browser language)
      const browserLang = navigator.language || (navigator as any).userLanguage || "";
      
      // Check if Polish (pl, pl-PL, etc.)
      if (browserLang.toLowerCase().startsWith("pl")) {
        return "pl";
      }
      
      // Also check navigator.languages array for Polish anywhere in preferences
      if (navigator.languages && navigator.languages.length > 0) {
        const hasPolish = navigator.languages.some(lang => 
          lang.toLowerCase().startsWith("pl")
        );
        if (hasPolish) {
          return "pl";
        }
      }
      
      // Default to English for all other languages
      return "en";
    };

    setLanguage(detectLanguage());
  }, []);

  return language;
}

/**
 * Get language from URL parameter or browser settings
 * Priority: 1. URL parameter (?lang=en|pl), 2. Browser language
 */
export function useLanguageWithOverride(): { language: Language; setLanguage: (lang: Language) => void } {
  const [language, setLanguage] = useState<Language>(() => {
    // Check URL param first
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const langParam = urlParams.get("lang");
      if (langParam === "en" || langParam === "pl") {
        return langParam;
      }
      
      // Fallback to browser language detection
      const browserLang = navigator.language || (navigator as any).userLanguage || "";
      if (browserLang.toLowerCase().startsWith("pl")) {
        return "pl";
      }
      
      if (navigator.languages && navigator.languages.length > 0) {
        const hasPolish = navigator.languages.some(lang => 
          lang.toLowerCase().startsWith("pl")
        );
        if (hasPolish) {
          return "pl";
        }
      }
    }
    return "en";
  });

  return { language, setLanguage };
}
